import React, { useContext } from 'react';
import { AppContextProvider } from '../AppLoader';
import { dataHooks } from '../../dataHooks';
import { ROUTES } from '../../routes';
import { Loader, Modal } from '@wix/design-system';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { AppRoutes } from '../../routing/AppRoutes';
import s from './App.scss';
import { PRESETS } from '../../utils/presets';
import classNames from 'classnames';

interface AppProps extends WithTranslation {}

const App: React.FC<AppProps> = observer(() => {
  const {
    rootStore: {
      isLoading,
      displayStore: {
        preset,
        presetKey,
        isFullScreenMode,
        isIframe,
        isDialogCentered,
        isMobileFullScreen,
      },
      onClickRootElement,
    },
  } = useContext(AppContextProvider);
  const { pathname } = useLocation();
  const useWhiteBackground =
    isMobileFullScreen || pathname === ROUTES.EMAIL_VERIFICATION_RESULT;

  // Class-based page background (e.g. Symphony's bundled marketing image),
  // used instead of the inline `background` string. Skipped when a white
  // background is forced (mobile / email-verification).
  const backgroundClass =
    !useWhiteBackground && preset.backgroundClassName
      ? s[preset.backgroundClassName]
      : undefined;

  return (
    <div
      data-hook={dataHooks.app.root}
      // TODO - Remove when finish investigating new login failure
      onClick={(e) => onClickRootElement(e)}
      className={classNames(s.root, backgroundClass, {
        // Stable Symphony marker (independent of the background class, which is
        // skipped on mobile / white-bg screens) — used to scope preset styles
        // (secondary-button and input states) to the Symphony preset alone.
        [s.symphonyPreset]: presetKey === PRESETS.SYMPHONY,
        [s.fadePreset]: presetKey === PRESETS.VIBU,
        [s.iframe]: isIframe,
        [s.modalMode]: !isFullScreenMode,
        dialogCentered: isDialogCentered,
        [s.mobileFullScreen]: isMobileFullScreen,
      })}
      style={{
        background: useWhiteBackground
          ? '#ffffff'
          : backgroundClass
          ? undefined // handled by backgroundClass above
          : (preset.background as any),
        minHeight: '100vh',
      }}
    >
      <Modal isOpen={isLoading}>
        <Loader />
      </Modal>
      <AppRoutes />
    </div>
  );
});

export default withTranslation()(App);
